// src/services/edgeFunctionService.js
import axios from 'axios';

/**
 * Invoke an edge function with the given parameters
 * @param {Object} params - The function parameters
 * @param {string} params.denoUrl - The deno URL of the edge function (from API response)
 * @param {string} params.edgeJwt - The edge function JWT token
 * @param {string} params.apiKey - The API key
 * @param {string} [params.playerToken] - Optional player token
 * @param {Object} params.payload - The payload to send (will be wrapped in { data: ... })
 * @param {Object} [params.customHeaders] - Optional custom headers
 * @returns {Promise<Object>} The response object
 */
export const invokeEdgeFunction = async ({
  denoUrl,
  edgeJwt,
  apiKey,
  playerToken,
  payload,
  customHeaders = {}
}) => {
  const url = denoUrl;
  const startTime = Date.now();

  const headers = {
    'Content-Type': 'application/json',
    'Authorization': `Bearer ${edgeJwt}`,
    'x-api-key': apiKey,
    ...customHeaders
  };

  if (playerToken) {
    headers['x-player-token'] = `Bearer ${playerToken}`;
  }

  // Send payload as-is
  const requestBody = typeof payload === 'string' ? JSON.parse(payload) : payload;

  try {
    const response = await axios.post(url, requestBody, { headers });
    const endTime = Date.now();

    return {
      success: true,
      data: response.data,
      status: response.status,
      statusText: response.statusText,
      headers: response.headers,
      responseTime: endTime - startTime,
      request: {
        url,
        method: 'POST',
        headers,
        body: requestBody
      }
    };
  } catch (error) {
    const endTime = Date.now();

    if (error.response) {
      // Server responded with error status
      return {
        success: false,
        data: error.response.data,
        status: error.response.status,
        statusText: error.response.statusText,
        headers: error.response.headers,
        responseTime: endTime - startTime,
        error: error.message,
        request: {
          url,
          method: 'POST',
          headers,
          body: requestBody
        }
      };
    }

    // Network error or other issue
    return {
      success: false,
      data: { error: error.message },
      status: 0,
      statusText: 'Network Error',
      headers: {},
      responseTime: endTime - startTime,
      error: error.message,
      request: {
        url,
        method: 'POST',
        headers,
        body: requestBody
      }
    };
  }
};

/**
 * Generate a cURL command for the edge function request
 * @param {Object} params - The function parameters
 * @returns {string} The cURL command
 */
export const generateEdgeFunctionCurl = ({
  denoUrl,
  edgeJwt,
  apiKey,
  playerToken,
  payload,
  customHeaders = {}
}) => {
  const url = denoUrl;

  let parsedPayload = {};
  try {
    parsedPayload = typeof payload === 'string' ? JSON.parse(payload) : payload;
  } catch (e) {
    parsedPayload = {};
  }

  const body = JSON.stringify(parsedPayload, null, 2);

  let curl = `curl -X POST '${url}' \\\n`;
  curl += `  -H 'Content-Type: application/json' \\\n`;
  curl += `  -H 'Authorization: Bearer ${edgeJwt || '<edge-jwt>'}' \\\n`;
  curl += `  -H 'x-api-key: ${apiKey || '<api-key>'}'`;

  if (playerToken) {
    curl += ` \\\n  -H 'x-player-token: Bearer ${playerToken}'`;
  }

  // Add custom headers
  Object.entries(customHeaders).forEach(([key, value]) => {
    if (key && value) {
      curl += ` \\\n  -H '${key}: ${value}'`;
    }
  });

  curl += ` \\\n  -d '${body}'`;

  return curl;
};

export default {
  invokeEdgeFunction,
  generateEdgeFunctionCurl
};
